import { memo } from 'react';
import { Skeleton, SkeletonItem, tokens, makeStyles } from '@fluentui/react-components';
import { DEFAULT_ROW_HEIGHT, PROJECT_ROW_HEIGHT } from '../hooks/useVirtualRows';

const SKELETON_PROJECT_COUNT = 4;
const SKELETON_TASKS_PER_PROJECT = 2;

// Matches the real grid: 2 sticky cols + 7 date cols + total + allocation
const gridColumns = 'minmax(200px, 1fr) 200px repeat(7, 100px) 100px 100px';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
        minWidth: 0,
        overflowX: 'hidden',
        overflowY: 'hidden',
        opacity: 0,
        animationName: {
            from: { opacity: 0 },
            to: { opacity: 1 },
        },
        animationDuration: '150ms',
        animationTimingFunction: 'ease-in',
        animationDelay: '500ms',
        animationFillMode: 'forwards',
    },
    header: {
        display: 'grid',
        gridTemplateColumns: gridColumns,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: tokens.colorNeutralStroke2,
        backgroundColor: tokens.colorNeutralBackground1,
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    headerCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingLeft: '12px',
        paddingRight: '12px',
    },
    headerCellFirst: {
        justifyContent: 'flex-start',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: gridColumns,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: tokens.colorNeutralStroke2,
        alignItems: 'center',
    },
    rowProject: {
        backgroundColor: tokens.colorNeutralBackground3,
        minHeight: `${PROJECT_ROW_HEIGHT}px`,
    },
    rowTask: {
        backgroundColor: tokens.colorNeutralBackground1,
        minHeight: `${DEFAULT_ROW_HEIGHT}px`,
    },
    rowTotal: {
        backgroundColor: tokens.colorNeutralBackground3,
        borderTopWidth: '2px',
        borderTopStyle: 'solid',
        borderTopColor: tokens.colorNeutralStroke2,
        minHeight: `${DEFAULT_ROW_HEIGHT}px`,
        marginTop: 'auto',
    },
    cell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingLeft: '8px',
        paddingRight: '8px',
    },
    cellFirst: {
        justifyContent: 'flex-start',
        paddingLeft: '12px',
    },
});

const HeaderSkeleton = memo(function HeaderSkeleton({ styles }: { styles: ReturnType<typeof useStyles> }) {
    return (
        <div className={styles.header}>
            <div className={`${styles.headerCell} ${styles.headerCellFirst}`}>
                <SkeletonItem size={16} style={{ width: '60px' }} />
            </div>
            <div className={styles.headerCell}>
                <SkeletonItem size={16} style={{ width: '70px' }} />
            </div>
            {Array.from({ length: 7 }, (_, i) => (
                <div key={i} className={styles.headerCell} style={{ flexDirection: 'column', rowGap: '4px' }}>
                    <SkeletonItem size={12} style={{ width: '30px' }} />
                    <SkeletonItem size={12} style={{ width: '50px' }} />
                </div>
            ))}
            <div className={styles.headerCell}>
                <SkeletonItem size={16} style={{ width: '50px' }} />
            </div>
            <div className={styles.headerCell}>
                <SkeletonItem size={16} style={{ width: '56px' }} />
            </div>
        </div>
    );
});

const ProjectRowSkeleton = memo(function ProjectRowSkeleton({ styles, nameWidth }: { styles: ReturnType<typeof useStyles>; nameWidth: string }) {
    return (
        <div className={`${styles.row} ${styles.rowProject}`}>
            <div className={`${styles.cell} ${styles.cellFirst}`} style={{ columnGap: '8px' }}>
                <SkeletonItem size={12} style={{ width: '12px' }} />
                <SkeletonItem size={16} style={{ width: nameWidth }} />
            </div>
            <div className={styles.cell} />
            {Array.from({ length: 7 }, (_, i) => (
                <div key={i} className={styles.cell}>
                    <SkeletonItem size={16} style={{ width: '32px' }} />
                </div>
            ))}
            <div className={styles.cell}>
                <SkeletonItem size={16} style={{ width: '32px' }} />
            </div>
            <div className={styles.cell}>
                <SkeletonItem size={16} style={{ width: '28px' }} />
            </div>
        </div>
    );
});

const TaskRowSkeleton = memo(function TaskRowSkeleton({ styles, nameWidth }: { styles: ReturnType<typeof useStyles>; nameWidth: string }) {
    return (
        <div className={`${styles.row} ${styles.rowTask}`}>
            <div className={`${styles.cell} ${styles.cellFirst}`} style={{ paddingLeft: '36px', columnGap: '8px' }}>
                <SkeletonItem size={16} style={{ width: nameWidth }} />
            </div>
            <div className={styles.cell}>
                <SkeletonItem size={16} style={{ width: '80px' }} />
            </div>
            {Array.from({ length: 7 }, (_, i) => (
                <div key={i} className={styles.cell}>
                    <SkeletonItem size={20} style={{ width: '48px', borderRadius: tokens.borderRadiusMedium }} />
                </div>
            ))}
            <div className={styles.cell}>
                <SkeletonItem size={16} style={{ width: '32px' }} />
            </div>
            <div className={styles.cell}>
                <SkeletonItem size={16} style={{ width: '28px' }} />
            </div>
        </div>
    );
});

const TotalRowSkeleton = memo(function TotalRowSkeleton({ styles }: { styles: ReturnType<typeof useStyles> }) {
    return (
        <div className={`${styles.row} ${styles.rowTotal}`}>
            <div className={`${styles.cell} ${styles.cellFirst}`}>
                <SkeletonItem size={16} style={{ width: '40px' }} />
            </div>
            <div className={styles.cell} />
            {Array.from({ length: 7 }, (_, i) => (
                <div key={i} className={styles.cell}>
                    <SkeletonItem size={16} style={{ width: '28px' }} />
                </div>
            ))}
            <div className={styles.cell}>
                <SkeletonItem size={16} style={{ width: '36px' }} />
            </div>
            <div className={styles.cell}>
                <SkeletonItem size={16} style={{ width: '28px' }} />
            </div>
        </div>
    );
});

const projectNameWidths = ['160px', '130px', '190px', '140px'];
const taskNameWidths = [
    ['120px', '150px'],
    ['180px', '100px'],
    ['140px', '160px'],
    ['110px', '130px'],
];

export const TimeGridSkeleton = memo(function TimeGridSkeleton() {
    const styles = useStyles();

    return (
        <Skeleton aria-label="Loading time grid">
            <div className={styles.root}>
                <HeaderSkeleton styles={styles} />
                {Array.from({ length: SKELETON_PROJECT_COUNT }, (_, projectIndex) => (
                    <div key={projectIndex}>
                        <ProjectRowSkeleton styles={styles} nameWidth={projectNameWidths[projectIndex]} />
                        {Array.from({ length: SKELETON_TASKS_PER_PROJECT }, (_, taskIndex) => (
                            <TaskRowSkeleton key={taskIndex} styles={styles} nameWidth={taskNameWidths[projectIndex][taskIndex]} />
                        ))}
                    </div>
                ))}
                <TotalRowSkeleton styles={styles} />
            </div>
        </Skeleton>
    );
});
